<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none bg-gray-100" tabindex="0">
        <form @submit.prevent="saveTicket">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div class="rounded-2xl bg-white border border-gray-200 shadow-sm p-5 md:p-6">
                    <div class="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div class="min-w-0">
                            <h1 class="text-xl font-semibold text-gray-900">{{ $t('Edit ticket') }}</h1>
                            <p class="mt-1 text-sm text-gray-500">
                                {{ $t('Update ticket details, executors, and approval') }}.
                            </p>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <span class="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold">
                                {{ $t('Executors') }}: {{ targetSelectionCount }}
                            </span>
                            <span class="inline-flex items-center px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
                                {{ $t('Approvers') }}: {{ approvalSelectionCount }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="mt-6 rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden">
                    <loading :status="loading.form" />

                    <div class="p-4 sm:p-6 space-y-6">
                        <section class="grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('Ticket details') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('Basic ticket information') }}.</p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <div>
                                    <label class="block text-sm font-medium text-gray-700" for="subject">{{ $t('Subject') }}</label>
                                    <input
                                        id="subject"
                                        v-model.trim="ticket.subject"
                                        :placeholder="$t('Subject')"
                                        class="mt-1 form-input block w-full text-sm"
                                        required
                                    >
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700">{{ $t('Status') }}</label>
                                        <select v-model="ticket.status_id" class="mt-1 form-select block w-full text-sm" required>
                                            <option v-for="status in statusList" :key="status.id" :value="status.id">
                                                {{ status.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700" for="ticket_started_at">{{ $t('Start date') }}</label>
                                        <input
                                            id="ticket_started_at"
                                            v-model="ticket.started_at"
                                            type="date"
                                            class="mt-1 form-input block w-full text-sm"
                                        >
                                        <p class="mt-1 text-xs text-gray-500">{{ $t('When work on this ticket starts') }}.</p>
                                    </div>
                                    <div>
                                        <label class="block text-sm font-medium text-gray-700" for="ticket_due_at">{{ $t('End date') }}</label>
                                        <input
                                            id="ticket_due_at"
                                            v-model="ticket.due_at"
                                            :min="ticket.started_at || undefined"
                                            type="date"
                                            class="mt-1 form-input block w-full text-sm"
                                        >
                                        <p class="mt-1 text-xs text-gray-500">{{ $t('Deadline for this ticket to be completed') }}.</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="border-t border-gray-200 pt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('Ticket executor') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">
                                    {{ $t('Select individual users, departments, or both') }}.
                                </p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <p class="flex items-start gap-1.5 text-xs text-gray-500">
                                    <svg-vue class="h-3.5 w-3.5 flex-shrink-0 text-blue-400" icon="font-awesome.info-circle-light"></svg-vue>
                                    <span>{{ $t('Your own account is excluded automatically') }}.</span>
                                </p>

                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per user') }}</label>
                                        <input
                                            v-model.trim="targetUserSearch"
                                            :placeholder="$t('Search user')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="user in filteredTargetUsers"
                                                :key="'target-user-option-' + user.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-blue-50 focus:outline-none"
                                                type="button"
                                                @click="addTargetUser(user.id)"
                                            >
                                                <img :src="getAvatar(user)" class="h-7 w-7 rounded-full mr-2 object-cover" alt="avatar">
                                                <span class="min-w-0 flex-1">
                                                    <span class="block text-sm text-gray-700 truncate">{{ user.name }}</span>
                                                    <span class="block text-xs text-gray-400 truncate">{{ user.email }}</span>
                                                </span>
                                            </button>
                                            <div v-if="filteredTargetUsers.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No users found') }}
                                            </div>
                                        </div>
                                    </div>

                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per department') }}</label>
                                        <input
                                            v-model.trim="targetDepartmentSearch"
                                            :placeholder="$t('Search department')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="department in filteredTargetDepartments"
                                                :key="'target-dept-option-' + department.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-blue-50 focus:outline-none"
                                                type="button"
                                                @click="addTargetDepartment(department.id)"
                                            >
                                                <span class="text-sm text-gray-700 truncate">{{ department.name }}</span>
                                            </button>
                                            <div v-if="filteredTargetDepartments.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No departments found') }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="rounded-xl border border-gray-200 bg-white p-3">
                                    <div class="flex items-center justify-between gap-3 mb-2">
                                        <div class="text-xs font-semibold text-gray-500">{{ $t('Selected executor users') }}</div>
                                        <div v-if="loadingTargetDepartmentUsers" class="text-xs text-blue-600">{{ $t('Loading department users') }}...</div>
                                    </div>
                                    <div v-if="selectedTargetDepartments.length > 0 && targetSelectionCount === 0" class="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-2">
                                        <div class="text-xs font-semibold text-gray-500 mb-2">{{ $t('Selected departments') }}</div>
                                        <div class="flex flex-wrap gap-2">
                                            <span
                                                v-for="department in selectedTargetDepartments"
                                                :key="'target-dept-chip-empty-' + department.id"
                                                class="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
                                            >
                                                {{ department.name }}
                                                <button class="ml-2 text-gray-500 hover:text-red-600" type="button" @click="removeTargetDepartment(department.id)">×</button>
                                            </span>
                                        </div>
                                        <div v-if="loadingTargetDepartmentUsers" class="mt-2 text-xs text-blue-600">
                                            {{ $t('Loading department users') }}...
                                        </div>
                                        <div v-else class="mt-2 text-xs text-red-600">
                                            {{ $t('No eligible executor users found in selected departments') }}.
                                        </div>
                                    </div>
                                    <div v-if="targetSelectionCount === 0" class="text-sm text-red-600">
                                        {{ $t('Select at least one executor') }}.
                                    </div>
                                    <div v-else class="space-y-3">
                                        <div class="flex flex-wrap gap-2">
                                            <span
                                                v-for="user in effectiveTargetUsers"
                                                :key="'target-effective-user-chip-' + user.id"
                                                class="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-800 text-xs border border-blue-100"
                                            >
                                                <img :src="getAvatar(user)" class="h-4 w-4 rounded-full mr-1 object-cover" alt="avatar">
                                                {{ user.name }}
                                                <span v-if="isDepartmentTargetUser(user.id)" class="ml-1 text-blue-500">{{ $t('dept') }}</span>
                                                <button
                                                    class="ml-2 text-blue-600 hover:text-red-600"
                                                    type="button"
                                                    @click="removeEffectiveTargetUser(user.id)"
                                                >×</button>
                                            </span>
                                        </div>
                                        <div v-if="selectedTargetDepartments.length > 0" class="pt-2 border-t border-gray-100">
                                            <div class="text-xs font-semibold text-gray-500 mb-2">{{ $t('Selected departments') }}</div>
                                            <div class="flex flex-wrap gap-2">
                                                <span
                                                    v-for="department in selectedTargetDepartments"
                                                    :key="'target-dept-chip-' + department.id"
                                                    class="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs border border-gray-200"
                                                >
                                                    {{ department.name }}
                                                    <button class="ml-2 text-gray-500 hover:text-red-600" type="button" @click="removeTargetDepartment(department.id)">×</button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section class="border-t border-gray-200 pt-6 grid grid-cols-1 lg:grid-cols-3 gap-5">
                            <div>
                                <h3 class="text-base font-semibold text-gray-900">{{ $t('Approval') }}</h3>
                                <p class="mt-1 text-sm text-gray-500">{{ $t('Configure ticket approval when required') }}.</p>
                            </div>
                            <div class="lg:col-span-2 space-y-4">
                                <input-switch
                                    id="approval_required"
                                    v-model="ticket.approval_required"
                                    :disabled-label="$t('No approval needed')"
                                    :enabled-label="$t('Approval needed')"
                                ></input-switch>

                                <div v-if="ticket.approval_required" class="space-y-4">
                                    <p class="flex items-start gap-1.5 text-xs text-gray-500">
                                        <svg-vue class="h-3.5 w-3.5 flex-shrink-0 text-blue-400" icon="font-awesome.info-circle-light"></svg-vue>
                                        <span>{{ $t('Select individual approvers. Your own account is excluded automatically') }}.</span>
                                    </p>

                                    <div class="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                        <label class="block text-xs font-semibold text-gray-600 mb-2">{{ $t('Per user') }}</label>
                                        <input
                                            v-model.trim="approverSearch"
                                            :placeholder="$t('Search approver')"
                                            class="form-input block w-full text-sm"
                                            type="text"
                                        >
                                        <div class="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                                            <button
                                                v-for="user in filteredApprovers"
                                                :key="'approver-user-option-' + user.id"
                                                class="w-full flex items-center px-3 py-2 text-left hover:bg-emerald-50 focus:outline-none"
                                                type="button"
                                                @click="addApproverUser(user.id)"
                                            >
                                                <img :src="getAvatar(user)" class="h-7 w-7 rounded-full mr-2 object-cover" alt="avatar">
                                                <span class="min-w-0 flex-1">
                                                    <span class="block text-sm text-gray-700 truncate">{{ user.name }}</span>
                                                    <span class="block text-xs text-gray-400 truncate">{{ user.email }}</span>
                                                </span>
                                            </button>
                                            <div v-if="filteredApprovers.length === 0" class="px-3 py-2 text-sm text-gray-500">
                                                {{ $t('No users found') }}
                                            </div>
                                        </div>
                                    </div>

                                        <div class="rounded-xl border border-gray-200 bg-white p-3">
                                        <div class="flex items-center justify-between gap-3 mb-2">
                                            <div class="text-xs font-semibold text-gray-500">{{ $t('Selected approver users') }}</div>
                                        </div>
                                        <div v-if="approvalSelectionCount === 0" class="text-sm text-red-600">
                                            {{ $t('Select at least one valid approver') }}.
                                        </div>
                                        <div v-else class="space-y-3">
                                            <div class="flex flex-wrap gap-2">
                                                <span
                                                    v-for="user in effectiveApprovers"
                                                    :key="'approver-effective-user-chip-' + user.id"
                                                    class="inline-flex items-center px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 text-xs border border-emerald-100"
                                                >
                                                    <img :src="getAvatar(user)" class="h-4 w-4 rounded-full mr-1 object-cover" alt="avatar">
                                                    {{ user.name }}
                                                    <button
                                                        class="ml-2 text-emerald-600 hover:text-red-600"
                                                        type="button"
                                                        @click="removeApproverUser(user.id)"
                                                    >×</button>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div class="bg-gray-50 border-t border-gray-200 px-4 py-3 sm:px-6">
                        <div class="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-3">
                            <span v-if="targetSelectionCount < 1" class="text-xs text-red-600 sm:mr-2">
                                {{ $t('Select at least one executor before submitting') }}.
                            </span>
                            <span v-else-if="ticket.approval_required && approvalSelectionCount < 1" class="text-xs text-red-600 sm:mr-2">
                                {{ $t('Select at least one valid approver before submitting') }}.
                            </span>
                            <span v-else class="text-xs text-gray-500 sm:mr-2">
                                {{ $t('Review executors and approval before submitting') }}.
                            </span>
                            <router-link class="btn btn-secondary shadow-sm rounded-md sm:mr-2 w-full sm:w-auto text-center" :to="'/dashboard/tickets/' + $route.params.uuid + '/manage'">
                                {{ $t('Cancel') }}
                            </router-link>
                            <button
                                :class="!canSubmitTicket || submitting ? 'opacity-50 cursor-not-allowed' : ''"
                                :disabled="!canSubmitTicket || submitting || loading.form"
                                class="btn btn-green shadow-sm rounded-md w-full sm:w-auto"
                                type="submit"
                            >
                                {{ submitting ? $t('Saving...') : $t('Edit ticket') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </main>
</template>

<script>
export default {
    name: "edit",
    metaInfo() {
        return {
            title: this.$i18n.t('Edit ticket')
        };
    },
    data() {
        return {
            loading: {
                form: true,
            },
            submitting: false,
            ticket: {
                subject: null,
                target_type: 'users',
                target_user_ids: [],
                target_department_ids: [],
                excluded_target_user_ids: [],
                status_id: 1,
                started_at: null,
                due_at: null,
                approval_required: false,
                approval_type: 'users',
                approval_user_ids: [],
                approval_department_ids: [],
                cc_user_ids: [],
            },
            targetUserList: [],
            targetDepartmentList: [],
            approverList: [],
            statusList: [],
            targetDepartmentUsers: [],
            loadingTargetDepartmentUsers: false,
            savedTargetUserIds: null,
            exclusionsHydrated: false,
            targetUserSearch: '',
            targetDepartmentSearch: '',
            approverSearch: '',
        };
    },
    computed: {
        authUserId() {
            return this.$store && this.$store.state.user ? Number(this.$store.state.user.id) : null;
        },
        normalizedTargetUserIds() {
            return this.normalizeIdArray(this.ticket.target_user_ids).filter((id) => id !== this.authUserId);
        },
        normalizedTargetDepartmentIds() {
            return this.normalizeIdArray(this.ticket.target_department_ids);
        },
        normalizedApprovalUserIds() {
            return this.normalizeIdArray(this.ticket.approval_user_ids).filter((id) => id !== this.authUserId);
        },
        selectedTargetUsers() {
            return this.usersByIds(this.targetUserList, this.normalizedTargetUserIds);
        },
        selectedApprovers() {
            return this.usersByIds(this.approverList, this.normalizedApprovalUserIds);
        },
        selectedTargetDepartments() {
            return this.itemsByIds(this.targetDepartmentList, this.normalizedTargetDepartmentIds);
        },
        targetDepartmentUserIds() {
            return this.targetDepartmentUsers
                .map((user) => Number(user.id))
                .filter((id) => !!id);
        },
        normalizedExcludedTargetUserIds() {
            return this.normalizeIdArray(this.ticket.excluded_target_user_ids);
        },
        activeDepartmentUsers() {
            const excludedIds = this.normalizedExcludedTargetUserIds;
            return this.targetDepartmentUsers.filter((user) => excludedIds.indexOf(Number(user.id)) === -1);
        },
        activeDepartmentUserIds() {
            return this.activeDepartmentUsers
                .map((user) => Number(user.id))
                .filter((id) => !!id);
        },
        effectiveTargetUsers() {
            return this.uniqueUsers(this.selectedTargetUsers.concat(this.activeDepartmentUsers));
        },
        effectiveApprovers() {
            return this.selectedApprovers;
        },
        targetSelectionCount() {
            return this.effectiveTargetUsers.length;
        },
        approvalSelectionCount() {
            if (!this.ticket.approval_required) {
                return 0;
            }
            return this.effectiveApprovers.length;
        },
        canSubmitTicket() {
            if (this.targetSelectionCount < 1) {
                return false;
            }
            if (this.ticket.approval_required && this.approvalSelectionCount < 1) {
                return false;
            }
            return true;
        },
        filteredTargetUsers() {
            // User yang sudah menjadi CC tidak boleh muncul lagi di daftar
            // eksekutor — satu orang satu peran per tiket.
            return this.filterUsers(
                this.targetUserList,
                this.targetUserSearch,
                this.mergeIds(
                    this.normalizedTargetUserIds,
                    this.activeDepartmentUserIds,
                    this.savedCcUserIds
                )
            );
        },
        filteredApprovers() {
            return this.filterUsers(
                this.approverList,
                this.approverSearch,
                this.mergeIds(
                    this.normalizedApprovalUserIds,
                    this.savedCcUserIds
                )
            );
        },
        savedCcUserIds() {
            return this.normalizeIdArray((this.ticket && this.ticket.cc_user_ids) || []);
        },
        filteredTargetDepartments() {
            return this.filterItems(this.targetDepartmentList, this.targetDepartmentSearch, this.normalizedTargetDepartmentIds);
        },
    },
    watch: {
        'ticket.approval_required'(value) {
            if (!value) {
                this.ticket.approval_user_ids = [];
            }
        },
    },
    mounted() {
        this.loadPage();
    },
    methods: {
        loadPage() {
            this.loading.form = true;
            Promise.all([this.getFilters(), this.getTicket()]).then(() => {
                this.sanitizeSelections();
                this.loading.form = false;
                this.sanitizeSelections();
                this.fetchTargetDepartmentUsers();
            }).catch(() => {
                this.loading.form = false;
            });
        },
        getFilters() {
            return axios.get('api/dashboard/tickets/filters', {
                params: {
                    context: 'assignment',
                    _t: Date.now(),
                }
            }).then((response) => {
                this.targetUserList = this.excludeInvalidUsers(response.data.targets || response.data.agents || []);
                this.targetDepartmentList = response.data.target_departments || response.data.departments || [];
                this.approverList = this.excludeInvalidUsers(response.data.approvers || response.data.agents || []);
                this.statusList = response.data.statuses || [];
                this.sanitizeSelections();
                this.fetchTargetDepartmentUsers();
            });
        },
        getTicket() {
            return axios.get('api/dashboard/tickets/' + this.$route.params.uuid, {
                params: {_t: Date.now()}
            }).then((response) => {
                if (!response.data.can_edit) {
                    this.$router.push('/dashboard/tickets/' + this.$route.params.uuid + '/manage');
                    return;
                }

                this.ticket = Object.assign({}, this.ticket, {
                    subject: response.data.subject,
                    target_type: 'mixed',
                    target_user_ids: response.data.target_user_ids || [],
                    target_department_ids: response.data.target_department_ids || [],
                    status_id: response.data.approval_required && response.data.approval_requested_status_id
                        ? response.data.approval_requested_status_id
                        : response.data.status_id,
                    started_at: response.data.started_at || null,
                    due_at: response.data.due_at || null,
                    approval_required: !!response.data.approval_required,
                    approval_type: 'users',
                    approval_user_ids: response.data.approval_user_ids || [],
                    approval_department_ids: [],
                    cc_user_ids: response.data.cc_user_ids || [],
                });
                // Simpan daftar executor tersimpan (hasil resolve backend) sebagai
                // acuan hidrasi: user departemen yang tidak ada di dalamnya berarti
                // pernah di-unselect manual.
                this.savedTargetUserIds = this.normalizeIdArray(response.data.target_user_ids || []);
            });
        },
        saveTicket() {
            if (this.submitting || this.loading.form) {
                return;
            }

            this.sanitizeSelections();

            if (!this.canSubmitTicket) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.targetSelectionCount < 1
                        ? this.$i18n.t('Select at least one executor').toString()
                        : this.$i18n.t('Select at least one valid approver').toString(),
                    type: 'error'
                });
                return;
            }

            this.submitting = true;
            this.loading.form = true;

            axios.put('api/dashboard/tickets/' + this.$route.params.uuid, this.buildTicketPayload()).then((response) => {
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t('Data saved correctly').toString(),
                    type: 'success'
                });
                this.$router.push('/dashboard/tickets/' + response.data.ticket.uuid + '/manage');
            }).catch(() => {
                this.loading.form = false;
            }).finally(() => {
                this.submitting = false;
            });
        },
        buildTicketPayload() {
            return Object.assign({}, this.ticket, {
                target_type: 'mixed',
                approval_type: 'mixed',
                target_user_ids: this.normalizedTargetUserIds,
                target_department_ids: this.normalizedTargetDepartmentIds,
                excluded_target_user_ids: this.normalizedExcludedTargetUserIds,
                approval_user_ids: this.ticket.approval_required ? this.normalizedApprovalUserIds : [],
                approval_department_ids: [],
            });
        },
        sanitizeSelections() {
            this.ticket.target_user_ids = this.normalizedTargetUserIds.filter((id) => {
                return this.activeDepartmentUserIds.indexOf(Number(id)) === -1;
            });
            this.ticket.target_department_ids = this.normalizedTargetDepartmentIds;
        },
        fetchTargetDepartmentUsers() {
            const departmentIds = this.normalizedTargetDepartmentIds;

            if (departmentIds.length < 1) {
                this.targetDepartmentUsers = [];
                this.hydrateExclusionsFromTicket();
                return;
            }

            this.loadingTargetDepartmentUsers = true;

            axios.get('api/dashboard/tickets/filters', {
                params: {
                    context: 'department-users',
                    purpose: 'target',
                    departments: departmentIds,
                    _t: Date.now(),
                }
            }).then((response) => {
                this.targetDepartmentUsers = this.excludeInvalidUsers(response.data.users || []);
                this.hydrateExclusionsFromTicket();
                this.removeTargetUsersCoveredByDepartments();
            }).finally(() => {
                this.loadingTargetDepartmentUsers = false;
            });
        },
        addTargetUser(userId) {
            const id = Number(userId);
            if (!id) {
                this.targetUserSearch = '';
                return;
            }

            // User ter-cover departemen terpilih: bila sedang di-exclude
            // (sempat di-unselect dari chip), pilih lagi = keluar dari
            // exclude supaya aktif kembali lewat departemen. Tanpa ini
            // klik pada opsinya diabaikan diam-diam.
            if (this.targetDepartmentUserIds.indexOf(id) > -1) {
                this.ticket.excluded_target_user_ids = this.removeId(this.ticket.excluded_target_user_ids, id);
                this.targetUserSearch = '';
                return;
            }

            // User yang sama boleh menjadi executor sekaligus approver pada ticket yang sama.
            this.ticket.target_user_ids = this.addUniqueId(this.ticket.target_user_ids, id);
            this.targetUserSearch = '';
        },
        removeTargetUser(userId) {
            this.ticket.target_user_ids = this.removeId(this.ticket.target_user_ids, userId);
        },
        removeEffectiveTargetUser(userId) {
            const id = Number(userId);
            if (this.isDirectTargetUser(id)) {
                this.removeTargetUser(id);
                return;
            }
            // User berasal dari departemen: masukkan ke daftar exclude agar
            // tidak ikut terpilih otomatis, tapi tetap bisa dipilih manual.
            this.ticket.excluded_target_user_ids = this.addUniqueId(this.ticket.excluded_target_user_ids, id);
            this.pruneEmptiedTargetDepartments();
        },
        pruneEmptiedTargetDepartments() {
            const excludedIds = this.normalizedExcludedTargetUserIds;
            const removed = [];
            this.normalizedTargetDepartmentIds.forEach((departmentId) => {
                const hasActiveUser = this.targetDepartmentUsers.some((user) => {
                    const deptIds = user.department_ids || [];
                    return deptIds.indexOf(Number(departmentId)) > -1
                        && excludedIds.indexOf(Number(user.id)) === -1;
                });
                if (!hasActiveUser) {
                    removed.push(Number(departmentId));
                }
            });
            if (removed.length < 1) {
                return;
            }
            // Departemen yang sudah tidak punya satu user pun di-unselect otomatis.
            this.ticket.target_department_ids = this.normalizedTargetDepartmentIds.filter((id) => {
                return removed.indexOf(Number(id)) === -1;
            });
            // Exclude user yang tidak lagi tercover departemen terpilih lain tidak perlu dipertahankan.
            const remainingDeptIds = this.normalizedTargetDepartmentIds;
            this.ticket.excluded_target_user_ids = this.normalizedExcludedTargetUserIds.filter((id) => {
                const user = this.targetDepartmentUsers.find((item) => Number(item.id) === Number(id));
                if (!user) {
                    return false;
                }
                const deptIds = user.department_ids || [];
                return remainingDeptIds.some((deptId) => deptIds.indexOf(Number(deptId)) > -1);
            });
            this.fetchTargetDepartmentUsers();
        },
        hydrateExclusionsFromTicket() {
            if (this.exclusionsHydrated || this.savedTargetUserIds === null) {
                return;
            }
            this.exclusionsHydrated = true;
            if (this.targetDepartmentUserIds.length < 1) {
                return;
            }
            // User departemen yang tidak ada di daftar executor tersimpan
            // berarti pernah di-unselect manual saat ticket dibuat/diedit.
            this.ticket.excluded_target_user_ids = this.targetDepartmentUserIds.filter((id) => {
                return this.savedTargetUserIds.indexOf(Number(id)) === -1;
            });
        },
        addTargetDepartment(departmentId) {
            this.ticket.target_department_ids = this.addUniqueId(this.ticket.target_department_ids, departmentId);
            this.targetDepartmentSearch = '';
            this.fetchTargetDepartmentUsers();
        },
        removeTargetDepartment(departmentId) {
            this.ticket.target_department_ids = this.removeId(this.ticket.target_department_ids, departmentId);
            this.fetchTargetDepartmentUsers();
        },
        addApproverUser(userId) {
            const id = Number(userId);
            if (!id) {
                this.approverSearch = '';
                return;
            }
            this.ticket.approval_user_ids = this.addUniqueId(this.ticket.approval_user_ids, id);
            this.approverSearch = '';
        },
        removeApproverUser(userId) {
            this.ticket.approval_user_ids = this.removeId(this.ticket.approval_user_ids, userId);
        },
        removeTargetUsersCoveredByDepartments() {
            const departmentUserIds = this.activeDepartmentUserIds;
            if (departmentUserIds.length < 1) {
                return;
            }
            this.ticket.target_user_ids = this.normalizedTargetUserIds.filter((id) => {
                return departmentUserIds.indexOf(Number(id)) === -1;
            });
        },
        mergeIds() {
            const seen = {};
            const result = [];
            Array.prototype.slice.call(arguments).forEach((values) => {
                (values || []).forEach((value) => {
                    const id = Number(value);
                    if (!id || seen[id]) {
                        return;
                    }
                    seen[id] = true;
                    result.push(id);
                });
            });
            return result;
        },
        addUniqueId(values, id) {
            const normalized = this.normalizeIdArray(values);
            id = Number(id);
            if (!id || normalized.indexOf(id) > -1) {
                return normalized;
            }
            normalized.push(id);
            return normalized;
        },
        removeId(values, id) {
            id = Number(id);
            return this.normalizeIdArray(values).filter((value) => value !== id);
        },
        normalizeIdArray(values) {
            if (!Array.isArray(values)) {
                values = values === null || values === undefined || values === '' ? [] : [values];
            }
            const seen = {};
            const result = [];
            values.forEach((value) => {
                const id = this.toId(value);
                if (!id || seen[id]) {
                    return;
                }
                seen[id] = true;
                result.push(id);
            });
            return result;
        },
        toId(value) {
            if (value && typeof value === 'object') {
                value = value.id;
            }
            const id = Number(value);
            return Number.isNaN(id) ? null : id;
        },
        excludeSelf(users) {
            return this.excludeInvalidUsers(users);
        },
        excludeInvalidUsers(users) {
            return (users || []).filter((user) => {
                return Number(user.id) !== this.authUserId && !this.isAdminUser(user);
            });
        },
        isAdminUser(user) {
            return !!(user && Number(user.role_id) === 1);
        },
        uniqueUsers(users) {
            const seen = {};
            return this.excludeInvalidUsers(users || []).filter((user) => {
                const id = Number(user.id);
                if (!id || seen[id]) {
                    return false;
                }
                seen[id] = true;
                return true;
            });
        },
        isDirectTargetUser(userId) {
            return this.normalizedTargetUserIds.indexOf(Number(userId)) > -1;
        },
        isDepartmentTargetUser(userId) {
            return this.targetDepartmentUsers.some((user) => Number(user.id) === Number(userId));
        },
        isDirectApproverUser(userId) {
            return this.normalizedApprovalUserIds.indexOf(Number(userId)) > -1;
        },
        usersByIds(users, ids) {
            return (users || []).filter((user) => ids.indexOf(Number(user.id)) > -1);
        },
        itemsByIds(items, ids) {
            return (items || []).filter((item) => ids.indexOf(Number(item.id)) > -1);
        },
        filterUsers(users, query, selectedIds) {
            return this.filterItems(users, query, selectedIds);
        },
        filterItems(items, query, selectedIds) {
            const text = String(query || '').toLowerCase().trim();
            return (items || [])
                .filter((item) => selectedIds.indexOf(Number(item.id)) === -1)
                .filter((item) => {
                    if (!text) {
                        return true;
                    }
                    return String(item.name || '').toLowerCase().indexOf(text) > -1
                        || String(item.email || '').toLowerCase().indexOf(text) > -1;
                })
                .slice(0, 30);
        },
        getAvatar(user) {
            if (!user) {
                return '';
            }
            return user.avatar && user.avatar !== 'gravatar' ? user.avatar : user.gravatar;
        },
    },
};
</script>
